import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {LimitExceededResponse, PaymentType, SubscriptionStatusDto} from '@core/models/payment.model';
import {PaymentFacadeService} from '@core/services/entities/payment/payment-facade.service';
import {CompanyService} from '@core/services/entities/company/company.service';
import {filter, take} from 'rxjs/operators';
import {
  CompanyRequisitesModalComponent,
  CompanyRequisitesModalData,
  CompanyRequisitesModalResult,
} from '../company-requisites-modal/company-requisites-modal.component';

const FALLBACK_AVG_DAYS_PER_MONTH = 30.4375;

/**
 * Конструктор докупки мест (expansion): +Δ users до текущего expiredAt.
 * Повторяет серверный расчёт из SumBehavior.applyExpansion (доля периода:
 * period.months × remaining/total — при полном неиспользованном периоде = period.months).
 */
@Component({
  selector: 'tariff-expansion-builder',
  templateUrl: './tariff-expansion-builder.component.html',
  styleUrls: ['./tariff-expansion-builder.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffExpansionBuilderComponent implements OnInit {
  @Input() payload: LimitExceededResponse;
  @Input() paymentType: PaymentType = 'bank_card';
  @Output() created = new EventEmitter<string>();

  addUsers = 1;
  companyId: string | null = null;
  status: SubscriptionStatusDto | null = null;

  constructor(
    private facade: PaymentFacadeService,
    private companyService: CompanyService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
  }

  get pricePerUserMonthly(): number {
    return this.payload?.tariff?.pricePerUserMonthly || 0;
  }

  get discount(): number {
    return this.status?.period?.discount ?? 0;
  }

  get remainingDays(): number {
    const expiredAt = this.status?.expiredAt;
    if (!expiredAt) {
      return 0;
    }
    const diff = new Date(expiredAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
  }

  get remainingMonthsFloat(): number {
    const status = this.status;
    const months = status?.period?.months ?? 0;
    if (!status?.expiredAt || months <= 0) {
      return 0;
    }
    const expiredTs = new Date(status.expiredAt).getTime();
    const remainingMs = expiredTs - Date.now();
    if (remainingMs <= 0) {
      return 0;
    }
    const startTs = status.currentPeriodStartedAt
      ? new Date(status.currentPeriodStartedAt).getTime()
      : expiredTs - months * (status.billingConstants?.avgDaysPerMonth || FALLBACK_AVG_DAYS_PER_MONTH) * 24 * 60 * 60 * 1000;
    const totalMs = expiredTs - startTs;
    if (totalMs <= 0) {
      return 0;
    }
    return months * (remainingMs / totalMs);
  }

  get effectivePricePerUserMonthly(): number {
    if (!this.pricePerUserMonthly) {
      return 0;
    }
    if (this.discount <= 0) {
      return this.pricePerUserMonthly;
    }
    return Math.max(0, Math.round(this.pricePerUserMonthly - (this.pricePerUserMonthly * this.discount / 100)));
  }

  get total(): number {
    return Math.max(0, Math.round(this.effectivePricePerUserMonthly * Math.max(this.addUsers, 0) * this.remainingMonthsFloat));
  }

  ngOnInit(): void {
    this.addUsers = this.payload.upsell.suggestedAddUsers || 1;

    this.companyService.getCompanyId().pipe(
      filter((id: string | null) => !!id),
      take(1),
    ).subscribe((id: string) => {
      this.companyId = id;
      this.facade.subscriptionStatus(id).subscribe(s => {
        this.status = s;
        this.cdr.markForCheck();
      });
    });
  }

  submit(): void {
    if (!this.companyId || !this.payload.tariff) {
      return;
    }

    // Безналичный счёт фиксирует снимок реквизитов в момент создания — форму
    // плательщика показываем до создания счёта (см. RequisitesSnapshotBehavior).
    if (this.paymentType === 'invoice') {
      this._requireRequisitesThen(() => this._createExpansion());
      return;
    }

    this._createExpansion();
  }

  private _createExpansion(): void {
    if (!this.companyId || !this.payload.tariff) {
      return;
    }

    this.facade.createExpansion({
      companyId: this.companyId,
      tariffId: this.payload.tariff.id,
      addUsers: this.addUsers,
      extendMonths: 0,
      paymentType: this.paymentType,
    }).subscribe(invoice => {
      if (this.paymentType === 'bank_card') {
        this.facade.tinkoffInit(invoice.id).subscribe(res => {
          window.location.href = res.paymentUrl;
        });
        return;
      }

      if (this.paymentType === 'test') {
        this.facade.testPay(invoice.id).subscribe(() => this.created.emit(invoice.id));
        return;
      }

      this.created.emit(invoice.id);
    });
  }

  private _requireRequisitesThen(action: () => void): void {
    const data: CompanyRequisitesModalData = {
      tariffName: this.payload.tariff?.title || 'MyBase – премиум подписка',
      periodLabel: `Докупка ${this.addUsers} мест`,
      priceLabel: `${this.total.toLocaleString('ru-RU')} ₽`,
    };

    const ref = this.dialog.open<CompanyRequisitesModalComponent, CompanyRequisitesModalData, CompanyRequisitesModalResult>(
      CompanyRequisitesModalComponent,
      {
        data,
        panelClass: 'requisites-dialog',
        width: '560px',
        maxWidth: '95vw',
        autoFocus: false,
      },
    );

    ref.afterClosed().subscribe(result => {
      if (result?.saved) {
        action();
      }
    });
  }
}
